// @ts-check
/**
 * Typed client for the entities API.
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 *
 *     npm run generate:types-from-openapi-schema
 */
import axios from 'axios'

/** @typedef {import('./schema').components['schemas']} Schemas */

/** @typedef {Schemas['Entity']} Entity */
/** @typedef {Schemas['EntityAttribute']} EntityAttribute */
/** @typedef {Schemas['EntityUpdateFieldRequest']} EntityUpdateFieldRequest */
/** @typedef {Schemas['EntityUpdateFieldResponse']} EntityUpdateFieldResponse */
/** @typedef {Schemas['UserEntityAssignment']} UserEntityAssignment */
/** @typedef {Schemas['UnlinkUserResponse']} UnlinkUserResponse */

/**
 * GET /api/entities/{id}/ — fetch entity details including attribute values.
 * @param {number} id
 * @returns {Promise<Entity>}
 */
export async function getEntity(id) {
  const { data } = await axios.get(`/api/entities/${id}/`)
  return data
}

/**
 * GET /api/entity-attributes/ — fetch attribute definitions for an entity type.
 * @param {number} entityTypeId
 * @returns {Promise<EntityAttribute[]>}
 */
export async function getEntityAttributeDefinitions(entityTypeId) {
  const { data } = await axios.get('/api/entity-attributes/', {
    params: { entity_type_id: entityTypeId },
  })
  return data
}

/**
 * GET /api/entities/appuser-assignments/ — list all users with their entity links.
 * @returns {Promise<UserEntityAssignment[]>}
 */
export async function getUserEntityAssignments() {
  const { data } = await axios.get('/api/entities/appuser-assignments/')
  return data
}

/**
 * POST /api/entities/{id}/unlink-user/ — unlink an entity from its user without deleting.
 * @param {number} entityId
 * @returns {Promise<UnlinkUserResponse>}
 */
export async function unlinkEntityUser(entityId) {
  const { data } = await axios.post(`/api/entities/${entityId}/unlink-user/`)
  return data
}

/**
 * POST /api/entities/assign-to-user/ — assign an entity to a user.
 * @param {{ appuser_id: number, entity_type_id: number, existing_entity_id?: number|null, parent_entity_id?: number|null }} payload
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export async function assignEntityToUser(payload) {
  const { data } = await axios.post('/api/entities/assign-to-user/', payload)
  return data
}

/**
 * POST /api/entities/bulk-assign/ — assign an entity type to multiple users.
 * @param {{ entity_type_id: number, appuser_ids: number[] }} payload
 * @returns {Promise<{ success: boolean, summary: { created: number, skipped: number, errors: number } }>}
 */
export async function bulkAssignEntities(payload) {
  const { data } = await axios.post('/api/entities/bulk-assign/', payload)
  return data
}

/**
 * GET /api/entities/user-assignable-types/ — entity types available for user assignment.
 * @returns {Promise<Array<{ id: number, type_name: string }>>}
 */
export async function getUserAssignableTypes() {
  const { data } = await axios.get('/api/entities/user-assignable-types/')
  return data
}

/**
 * GET /api/entities/unlinked-entities/ — entities of a type not linked to any user.
 * @param {number} entityTypeId
 * @returns {Promise<Array<{ id: number, name: string }>>}
 */
export async function getUnlinkedEntities(entityTypeId) {
  const { data } = await axios.get('/api/entities/unlinked-entities/', {
    params: { entity_type_id: entityTypeId },
  })
  return data
}

/**
 * Fetch entity details and attribute definitions in sequence, then merge into
 * a single attributes dict with a `_defined_attributes` list so consumers can
 * distinguish "attribute not defined for this entity type" from "defined but
 * no value set yet."
 *
 * @param {number} entityId
 * @returns {Promise<{ attributes: Record<string, any>, entity: Entity }>}
 */
export async function getEntityWithAttributeDefinitions(entityId) {
  const entity = await getEntity(entityId)
  const definitions = await getEntityAttributeDefinitions(entity.entity_type)
  const attributes = entity.attributes || {}
  attributes['_defined_attributes'] = definitions.map((d) => d.name)
  return { attributes, entity }
}
